// apps/web/src/lib/vendas/erro-api.ts — Fase 12, WR-D1-12 (D1) + mensagens dos 409/400 novos das Partes A/B.
// Leitura ÚNICA do corpo de erro da API de Vendas: 403 vira texto em português (o Nest devolve "Forbidden resource"),
// `message` string ou array é normalizado (primeira mensagem) e os códigos de negócio ganham mensagem própria em pt-BR.
// Funções puras e testáveis; `lerErroApi` só lê o JSON da resposta.

import type { AtalhoCadastroDto } from '@pieltec/contracts';

export const MENSAGEM_SEM_PERMISSAO_PV = 'Você não tem permissão para esta ação.';
export const MENSAGEM_FALHA_PV = 'Não foi possível concluir a operação. Tente novamente.';

export interface PvJaImportado {
  readonly id: string;
  readonly numero: string;
}

export interface ErroApi {
  readonly status: number;
  readonly codigo: string | null;
  /** Já em português e pronta para a tela. */
  readonly mensagem: string;
  /** RASCUNHO_FISCAL_INCOMPATIVEL_OMIE: o que falta para a Omie aceitar a emissão. */
  readonly faltas: readonly string[];
  /** IMPORTACAO_JA_CONFIRMADA: PVs que a mesma planilha já criou. */
  readonly jaImportado: readonly PvJaImportado[];
  /** Fase 17.3 (D-25): para onde a tela leva o PCP quando o produto não tem cadastro ou a peça não tem roteiro; null nos demais erros. */
  readonly atalho: AtalhoCadastroDto | null;
}

export const CODIGO_IMPORTACAO_JA_CONFIRMADA = 'IMPORTACAO_JA_CONFIRMADA';
export const CODIGO_ITEM_RECEBIDO_TROCAR_MATERIAL = 'PV_ITEM_RECEBIDO_TROCAR_MATERIAL';
export const CODIGO_PV_CONCORRENCIA = 'PV_CONCORRENCIA';
export const CODIGO_RASCUNHO_INCOMPATIVEL_OMIE = 'RASCUNHO_FISCAL_INCOMPATIVEL_OMIE';
export const CODIGO_NOTA_OUTRO_CLIENTE = 'PV_NOTA_OUTRO_CLIENTE';
const CODIGOS_PERIODO_INVALIDO: readonly string[] = ['PERIODO_INVALIDO', 'PV_PERIODO_INVALIDO'];

export const MENSAGEM_TROCAR_MATERIAL =
  'O item já foi recebido, então o material não pode ser vinculado por aqui. Abra o menu ⋯ do item, escolha "Ajustar" e use "Trocar cor/material" (exige a permissão de ajuste do PV).';
export const MENSAGEM_PV_CONCORRENCIA = 'Outra operação alterou este PV ao mesmo tempo. Nada foi perdido: recarregue o PV e tente novamente.';
export const MENSAGEM_NOTA_OUTRO_CLIENTE = 'A nota fiscal pertence a outro cliente. Escolha o cliente da nota ou selecione outra nota.';
export const MENSAGEM_PERIODO_INVALIDO = 'Período inválido. Informe datas reais e mantenha a data inicial igual ou anterior à final.';

function textoDe(valor: unknown): string | null {
  if (typeof valor === 'string' && valor.trim() !== '') return valor;
  if (Array.isArray(valor)) {
    const primeira = valor.find((item): item is string => typeof item === 'string' && item.trim() !== '');
    return primeira ?? null;
  }
  return null;
}

function listaDeTextos(valor: unknown): string[] {
  return Array.isArray(valor) ? valor.filter((item): item is string => typeof item === 'string' && item.trim() !== '') : [];
}

function listaDeJaImportados(valor: unknown): PvJaImportado[] {
  if (!Array.isArray(valor)) return [];
  const itens: PvJaImportado[] = [];
  for (const candidato of valor) {
    const registro = candidato as { id?: unknown; numero?: unknown } | null;
    if (registro && typeof registro.numero === 'string') {
      itens.push({ id: typeof registro.id === 'string' ? registro.id : '', numero: registro.numero });
    }
  }
  return itens;
}

function atalhoDe(valor: unknown): AtalhoCadastroDto | null {
  if (!valor || typeof valor !== 'object') return null;
  const registro = valor as Record<string, unknown>;
  if (registro.tipo !== 'CADASTRAR_PECA' && registro.tipo !== 'CADASTRAR_ROTEIRO') return null;
  const texto = (chave: string): string | undefined => {
    const campo = registro[chave];
    return typeof campo === 'string' && campo !== '' ? campo : undefined;
  };
  return {
    tipo: registro.tipo,
    pedidoVendaId: texto('pedidoVendaId'),
    itemId: texto('itemId'),
    materialId: texto('materialId'),
    clienteId: texto('clienteId'),
    codigoItemCliente: texto('codigoItemCliente'),
    pendenciaId: texto('pendenciaId'),
  };
}

/** Junta os números dos PVs ("PV-1, PV-2"). */
export function numerosDosPvs(pvs: readonly PvJaImportado[]): string {
  return pvs.map((pv) => pv.numero).join(', ');
}

/** Interpreta status + corpo (já lido) em um erro pronto para a tela. `corpo` pode ser qualquer coisa (não-JSON vira null). */
export function interpretarErroApi(status: number, corpo: unknown, padrao: string = MENSAGEM_FALHA_PV): ErroApi {
  const dados = (corpo && typeof corpo === 'object' ? corpo : {}) as Record<string, unknown>;
  const codigo = typeof dados.codigo === 'string' ? dados.codigo : null;
  const mensagemApi = textoDe(dados.message);
  const faltas = listaDeTextos(dados.faltas);
  const jaImportado = listaDeJaImportados(dados.jaImportado);
  const base = { status, codigo, faltas, jaImportado, atalho: atalhoDe(dados.atalho) };

  if (status === 403) return { ...base, mensagem: MENSAGEM_SEM_PERMISSAO_PV };

  if (codigo === CODIGO_IMPORTACAO_JA_CONFIRMADA) {
    const pvs = numerosDosPvs(jaImportado);
    const criados = pvs ? ` PVs já criados: ${pvs}.` : '';
    return {
      ...base,
      mensagem: `Este arquivo já foi importado, então nada novo foi criado.${criados} Para importar de novo, cancele esses PVs antes.`,
    };
  }
  if (codigo === CODIGO_ITEM_RECEBIDO_TROCAR_MATERIAL) return { ...base, mensagem: MENSAGEM_TROCAR_MATERIAL };
  if (codigo === CODIGO_PV_CONCORRENCIA) return { ...base, mensagem: MENSAGEM_PV_CONCORRENCIA };
  if (codigo === CODIGO_NOTA_OUTRO_CLIENTE) return { ...base, mensagem: MENSAGEM_NOTA_OUTRO_CLIENTE };
  if (codigo !== null && CODIGOS_PERIODO_INVALIDO.includes(codigo)) {
    return { ...base, mensagem: mensagemApi ? `${MENSAGEM_PERIODO_INVALIDO} (${mensagemApi})` : MENSAGEM_PERIODO_INVALIDO };
  }
  if (codigo === CODIGO_RASCUNHO_INCOMPATIVEL_OMIE) {
    const falta = faltas.length > 0 ? ` Falta: ${faltas.join('; ')}.` : '';
    return {
      ...base,
      mensagem: `A Omie recusaria a emissão deste rascunho, então ele não foi aprovado.${falta} Corrija (ajuste os valores ou o modelo fiscal), recalcule e aprove de novo.`,
    };
  }
  return { ...base, mensagem: mensagemApi ?? padrao };
}

/** Lê o corpo da resposta de erro (JSON ou não) e devolve o erro pronto para a tela. */
export async function lerErroApi(res: Response, padrao: string = MENSAGEM_FALHA_PV): Promise<ErroApi> {
  let corpo: unknown = null;
  try {
    corpo = await res.json();
  } catch {
    // Corpo vazio ou não-JSON: vale a mensagem padrão.
  }
  return interpretarErroApi(res.status, corpo, padrao);
}
